import React, { useEffect, useMemo, useState } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Customer, Project, ProjectTag } from '../types';
import { api, customersAPI, projectTagsAPI, projectsAPI } from '../services/api';
import ProjectsTable from './ProjectsTable';
import Pagination from './Pagination';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchableDropdown from './SearchableDropdown';
import { usePagination } from '../hooks/usePagination';
import { addRedirectToUrl } from '../utils/redirectUtils';
import { loadFiltersFromStorage, saveFiltersToStorage } from '../utils/filterStorage';

type StatusItem = { id: number; title: string };

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // (no query-string filters)
  const confirm = useConfirm();

  const [projects, setProjects] = useState<Project[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projectTags, setProjectTags] = useState<ProjectTag[]>([]);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(true);

  // Load initial filters from localStorage
  const defaultFilters = {
    customerFilter: 0,
    searchText: '',
    tagFilter: [],
  };
  const initialFilters = loadFiltersFromStorage('leads', defaultFilters);

  const [customerFilter, setCustomerFilter] = useState<number>(initialFilters.customerFilter);
  const [searchText, setSearchText] = useState<string>(initialFilters.searchText);
  const [tagFilter, setTagFilter] = useState<string[]>(initialFilters.tagFilter || []);

  const loadStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const response = await api.get('/statuses');
      const statusesData = response.data.data || response.data;
      setStatuses(statusesData);
    } catch (e) {
      console.error('Error loading statuses:', e);
      setStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsData, customersData, projectTagsData] = await Promise.all([
        projectsAPI.getProjects({ take: 10000 }),
        customersAPI.getCustomers(),
        projectTagsAPI.getProjectTags(),
      ]);
      setProjects(projectsData.items);
      setCustomers(customersData);
      setProjectTags(projectTagsData);
      setError(null);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
    loadData();
  }, []);

  const allTags = useMemo(() => (
    projectTags
      .map(tag => tag.name.trim())
      .filter(Boolean)
  ), [projectTags]);

  const tagOptions = useMemo(() => (
    allTags.map((tag, index) => ({ id: index + 1, name: tag }))
  ), [allTags]);

  const tagIdByName = useMemo(() => {
    return new Map(tagOptions.map(tag => [tag.name, tag.id]));
  }, [tagOptions]);

  const tagNameById = useMemo(() => {
    return new Map(tagOptions.map(tag => [tag.id, tag.name]));
  }, [tagOptions]);

  const confirmedCutoffStatusIds = useMemo(() => {
    if (!statuses || statuses.length === 0) return [] as number[];

    const cutoffIndex = statuses.findIndex(s => (s.title || '').trim().toLowerCase() === 'confirmed');
    if (cutoffIndex === -1) {
      // Fallback: include only "Lead" if Confirmed isn't found
      const lead = statuses.find(s => (s.title || '').trim().toLowerCase() === 'lead');
      return lead ? [lead.id] : [];
    }

    return statuses.slice(0, cutoffIndex + 1).map(s => s.id);
  }, [statuses]);

  const filteredProjects = useMemo(() => {
    const search = (searchText || '').toLowerCase();

    return projects.filter(project => {
      const matchesLeadPipeline = confirmedCutoffStatusIds.length === 0
        ? true
        : confirmedCutoffStatusIds.includes(project.statusId);

      const matchesCustomer = customerFilter === 0 || project.customerId === customerFilter;

      const projectTagNames = (project.projectTags || [])
        .map(tag => tag?.name)
        .filter((name): name is string => Boolean(name && name.trim()))
        .map(name => name.trim());

      const matchesTags = tagFilter.length === 0 || projectTagNames.some(tag => tagFilter.includes(tag));

      const matchesSearch =
        !search ||
        project.title.toLowerCase().includes(search) ||
        project.description?.toLowerCase().includes(search) ||
        project.po?.toLowerCase().includes(search) ||
        project.customerTitle?.toLowerCase().includes(search);

      return matchesLeadPipeline && matchesCustomer && matchesTags && matchesSearch;
    });
  }, [projects, confirmedCutoffStatusIds, customerFilter, tagFilter, searchText]);

  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [filteredProjects]);

  const {
    currentPage,
    pageSize,
    paginatedData: paginatedProjects,
    totalItems,
    goToPage,
    setPageSize,
  } = usePagination({ data: sortedProjects, initialPageSize: 10 });

  const customerOptions = useMemo(() => ([
    { value: 0, label: 'All customer' },
    ...customers.map(customer => ({ value: customer.id, label: customer.title })),
  ]), [customers]);

  const handleDeleteProject = async (id: number) => {
    const isConfirmed = await confirm({
      title: 'Delete project',
      message: 'Are you sure you want to delete this project? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      await projectsAPI.deleteProject(id);
      await loadData();
    } catch (err) {
      setError('Failed to delete project');
      console.error('Error deleting project:', err);
    }
  };

  const handleEditProject = (project: Project) => {
    navigate(addRedirectToUrl(`/projects/edit/${project.id}`, location.pathname + location.search));
  };

  const handleViewDetails = (id: number) => {
    navigate(`/projects/${id}`);
  };

  const handleNewTagCreated = (newTag: ProjectTag) => {
    setProjectTags(prev => [...prev, newTag]);
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '2rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 style={{
              color: 'var(--text-primary)',
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              Leads
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
              Projects up to Confirmed (inclusive)
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/projects/create?redirect=/leads')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            Create Project
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border" role="status"></div>
          <p className="text-muted">Loading leads...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h5 className="card-title">Lead List</h5>
              <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
                <div>
                  <input
                    type="text"
                    id="leadsSearchText"
                    className="form-control"
                    placeholder="Search leads..."
                    value={searchText}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSearchText(newValue);
                      saveFiltersToStorage('leads', {
                        customerFilter,
                        searchText: newValue,
                        tagFilter,
                      });
                    }}
                  />
                </div>
                <div style={{ minWidth: '200px' }}>
                  <SearchableDropdown
                    id="leadsCustomerFilter"
                    label=""
                    placeholder="All Customers"
                    items={customerOptions}
                    value={customerFilter}
                    onChange={(value) => {
                      const newValue = Number(value);
                      setCustomerFilter(newValue);
                      saveFiltersToStorage('leads', {
                        customerFilter: newValue,
                        searchText,
                        tagFilter,
                      });
                    }}
                    getItemValue={(item) => item.value}
                    getItemLabel={(item) => item.label}
                    searchPlaceholder="Search customers..."
                    noItemsMessage="No customers found"
                    className="filter-dropdown"
                    size="md"
                  />
                </div>
                <div style={{ minWidth: '200px' }}>
                  <MultiSelectDropdown
                    availableTags={tagOptions}
                    selectedTagIds={tagFilter
                      .map(tag => tagIdByName.get(tag))
                      .filter((id): id is number => typeof id === 'number')}
                    onSelectionChange={(newTagFilterIds) => {
                      const newTagFilter = newTagFilterIds
                        .map(id => tagNameById.get(id))
                        .filter((tag): tag is string => Boolean(tag));
                      setTagFilter(newTagFilter);
                      saveFiltersToStorage('leads', {
                        customerFilter,
                        searchText,
                        tagFilter: newTagFilter,
                      });
                    }}
                    placeholder="No tags selected"
                    showSelectedTags={false}
                    buttonStyle={{ minWidth: '200px' }}
                    onNewTagCreated={handleNewTagCreated as any}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <>
              <ProjectsTable
                projects={paginatedProjects}
                statuses={[{ id: 0, title: 'All status' }, ...statuses]}
                loadingStatuses={loadingStatuses}
                statusFilter={0}
                customerOptions={customerOptions}
                customerFilter={customerFilter}
                searchText={searchText}
                onlyWithoutTransactions={false}
                onSearchTextChange={(newValue: string) => {
                  setSearchText(newValue);
                  saveFiltersToStorage('leads', {
                    customerFilter,
                    searchText: newValue,
                    tagFilter,
                  });
                }}
                onStatusFilterChange={() => {}}
                onCustomerFilterChange={(newValue: number) => {
                  setCustomerFilter(newValue);
                  saveFiltersToStorage('leads', {
                    customerFilter: newValue,
                    searchText,
                    tagFilter,
                  });
                }}
                onOnlyWithoutTransactionsChange={() => {}}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onViewDetails={handleViewDetails}
              />

              {sortedProjects.length === 0 ? (
                <div className="text-center text-muted p-5">
                  <div style={{ fontSize: '3rem' }}>
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                  </div>
                  <h5>No leads found</h5>
                  <p>Try adjusting your search filters or create a new project.</p>
                </div>
              ) : (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              )}
            </>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
